import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Typography,
  Box,
  Stack,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/Merge';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';

const isMeaningfulAccountCaseId = (id) => {
  if (id == null) return false;
  const trimmed = String(id).trim();
  return trimmed !== '' && trimmed !== '0';
};

const formatCardChipLabel = (card, t) => {
  const parts = [];
  if (card.name) parts.push(`${card.name} (${card.cardId})`);
  else parts.push(String(card.cardId));
  if (card.palCode) parts.push(`${t('PalCode')}: ${card.palCode}`);
  if (isMeaningfulAccountCaseId(card.accountCaseId)) {
    parts.push(`${t('AccountCaseId')}: ${card.accountCaseId}`);
  }
  return parts.join(' • ');
};

const MergeSelectedCardsDialog = ({ open, onClose, cards, onConfirm, isProcessing }) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const [baseCardId, setBaseCardId] = useState(null);
  const [deleteOldCards, setDeleteOldCards] = useState(true);

  useEffect(() => {
    if (open && cards?.length) {
      setBaseCardId(String(cards[0].cardId));
      setDeleteOldCards(true);
    }
  }, [open, cards]);

  if (!cards?.length) return null;

  const baseCard = cards.find((c) => String(c.cardId) === String(baseCardId)) || cards[0];
  const toMerge = cards.filter((c) => String(c.cardId) !== String(baseCard.cardId));

  const handleConfirm = () => {
    if (toMerge.length === 0) return;
    onConfirm({
      baseCardId: baseCard.cardId,
      cardsToMerge: toMerge.map((c) => c.cardId),
      deleteOldCards,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      dir={direction}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <MergeIcon color="primary" />
        <Box>
          <Typography variant="h6" component="span" fontWeight={700}>
            {t('MergeSelected')}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {t('PickBaseCardHint')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 1 }}>
          {t('BaseCard')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mb: 2 }}>
          {cards.map((card) => {
            const isBase = String(card.cardId) === String(baseCard.cardId);
            return (
              <Tooltip key={card.cardId} title={isBase ? t('BaseCard') : t('ClickToSetAsBase')}>
                <Chip
                  size="small"
                  icon={isBase ? <StarIcon sx={{ fontSize: '16px !important' }} /> : undefined}
                  label={formatCardChipLabel(card, t) + (isBase ? ` • ${t('BaseCard')}` : '')}
                  color={isBase ? 'primary' : 'default'}
                  variant={isBase ? 'filled' : 'outlined'}
                  onClick={() => setBaseCardId(String(card.cardId))}
                  sx={{ cursor: 'pointer', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                />
              </Tooltip>
            );
          })}
        </Stack>

        {toMerge.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100' }}>
              {t('CardsToMerge')} ({toMerge.length})
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
              {toMerge.map((c) => c.name || c.cardId).join(' → ')}
            </Typography>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={deleteOldCards}
              onChange={(e) => setDeleteOldCards(e.target.checked)}
              color="primary"
              disabled={isProcessing}
            />
          }
          label={t('DeleteMergedCards')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isProcessing} variant="outlined">
          {t('cancelButton')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isProcessing || toMerge.length === 0}
          startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : <MergeIcon />}
        >
          {isProcessing ? t('Merging') : t('ConfirmMerge')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeSelectedCardsDialog;
